import {
  DEFAULT_GRID_COLUMNS,
  DEFAULT_GRID_ROWS,
} from '../../components/PageConfigProvider/constants';
import type { TabContent } from '../../components/PageConfigProvider/page-reducer';
import {
  type Page,
  PageType,
  RevelioPageId,
} from '../../components/PageConfigProvider/types';
import type { MetricValues } from '../../components/PageConfigProvider/types/metric';
import { FilterId } from '../../filters/types/filters';

const roleSentimentMetrics: MetricValues[] = [
  'overall',
  'business_outlook',
  'career_growth',
  'compensation',
  'benefits',
  'healthcare',
  'retirement_pension',
  'childcare',
  'education_assistance',
  'wellness_programs',
  'work_life_balance',
  'diversity_and_inclusion',
  'leadership',
  'culture',
  'remote_work',
  'onboarding',
  'mentorship',
  'technology',
  'promotion_opportunities',
  'paid_time_off',
  'transparency',
  'efficiency_and_bureaucracy',
  'product_and_service_quality',
  'prestige',
  'coworkers',
  'mission_and_vision',
  'workplace_facilities',
  'training',
  'clients_and_customers',
  'company_growth',
  'schedule_flexibility',
  'job_security',
  'overtime',
  'family_support',
  'commission',
  'independence',
  'commute',
  'safety',
  'internal_communication',
  'office_politics',
  'harassment',
  'honesty',
  'ethics',
  'commitment',
  'compliance',
  'integrity',
];
const sentimentInfoText =
  'Sentiment scores are derived from both the free text and the star ratings associated with employee reviews, and normalized so that 50% is the median score amongst all roles. Sample size includes all reviews with star ratings predictive of a specific sentiment topic, as well as all reviews that contain any signal for that sentiment topic in their free text, within the previous 12 months.';

const sentimentBarAxisRange = {
  maxValue: 1,
  minValue: 0,
};

export const roleSentimentSnapshotItems: TabContent['items'] = [
  {
    position: { x: 0, width: 11, y: 0, height: 12 },
    id: 'role-sentiment-snapshot-overall',
    title: '{{metric}}',
    info: {
      text: sentimentInfoText,
    },
    plot: {
      chartType: 'bar',
      defaultMetricOptions: roleSentimentMetrics,
      ...sentimentBarAxisRange,
    },
  },
  {
    position: { x: 11, width: 11, y: 0, height: 12 },
    id: 'role-sentiment-snapshot-business-outlook',
    title: '{{metric}}',
    info: {
      text: sentimentInfoText,
    },
    plot: {
      chartType: 'bar',
      defaultMetricOptions: roleSentimentMetrics,
      ...sentimentBarAxisRange,
    },
  },
  {
    position: { x: 22, width: 11, y: 0, height: 12 },
    id: 'role-sentiment-snapshot-career-growth',
    title: '{{metric}}',
    info: {
      text: sentimentInfoText,
    },
    plot: {
      chartType: 'bar',
      defaultMetricOptions: roleSentimentMetrics,
      ...sentimentBarAxisRange,
    },
  },
  {
    position: { x: 0, width: 11, y: 12, height: 12 },
    id: 'role-sentiment-snapshot-compensation',
    title: '{{metric}}',
    info: {
      text: sentimentInfoText,
    },
    plot: {
      chartType: 'bar',
      defaultMetricOptions: roleSentimentMetrics,
      ...sentimentBarAxisRange,
    },
  },
  {
    position: { x: 11, width: 11, y: 12, height: 12 },
    id: 'role-sentiment-snapshot-benefits',
    title: '{{metric}}',
    info: {
      text: sentimentInfoText,
    },
    plot: {
      chartType: 'bar',
      defaultMetricOptions: roleSentimentMetrics,
      ...sentimentBarAxisRange,
    },
  },
  {
    position: { x: 22, width: 11, y: 12, height: 12 },
    id: 'role-sentiment-snapshot-culture',
    title: '{{metric}}',
    info: {
      text: sentimentInfoText,
    },
    plot: {
      chartType: 'bar',
      defaultMetricOptions: roleSentimentMetrics,
      ...sentimentBarAxisRange,
    },
  },
  {
    position: { x: 0, width: 11, y: 24, height: 12 },
    id: 'role-sentiment-snapshot-work-life-balance',
    title: '{{metric}}',
    info: {
      text: sentimentInfoText,
    },
    plot: {
      chartType: 'bar',
      defaultMetricOptions: roleSentimentMetrics,
      ...sentimentBarAxisRange,
    },
  },
  {
    position: { x: 11, width: 11, y: 24, height: 12 },
    id: 'role-sentiment-snapshot-diversity-and-inclusion',
    title: '{{metric}}',
    info: {
      text: sentimentInfoText,
    },
    plot: {
      chartType: 'bar',
      defaultMetricOptions: roleSentimentMetrics,
      ...sentimentBarAxisRange,
    },
  },
  {
    position: { x: 22, width: 11, y: 24, height: 12 },
    id: 'role-sentiment-snapshot-leadership',
    title: '{{metric}}',
    info: {
      text: sentimentInfoText,
    },
    plot: {
      chartType: 'bar',
      defaultMetricOptions: roleSentimentMetrics,
      ...sentimentBarAxisRange,
    },
  },
  {
    position: { x: 33, width: 15, y: 0, height: 18 },
    id: 'role-sentiment-snapshot-positive-reviews',
    title: 'Positive Reviews',
    info: {
      text: 'An AI-generated summary of all positive reviews.',
    },
    plot: {
      defaultMetricOptions: ['positive_review'],
      isSingleEntity: true,
      chartType: 'sentiment_review',
    },
  },
  {
    position: { x: 33, width: 15, y: 18, height: 18 },
    id: 'role-sentiment-snapshot-negative-reviews',
    title: 'Negative Reviews',
    info: {
      text: 'An AI-generated summary of all negative reviews.',
    },
    plot: {
      defaultMetricOptions: ['negative_review'],
      isSingleEntity: true,
      chartType: 'sentiment_review',
    },
  },
];

export const roleSentimentSingleSnapshotItems: TabContent['items'] = [
  {
    position: { x: 0, width: 33, y: 0, height: 36 },
    id: 'role-sentiment-topics',
    title: 'Sentiment Topics',
    info: {
      text: 'Sentiment scores are derived from both the free text and the star ratings associated with employee reviews, and normalized so that 50% is the median score amongst all roles. Sample size includes all reviews with star ratings predictive of a specific sentiment topic, as well as all reviews that contain any signal for that sentiment topic in their free text, within the previous 12 months.',
    },
    plot: {
      defaultMetricOptions: ['sentiment_top_bottom'],
      chartType: 'diverging-bar',
      divergingBar: {
        negativeLabel: 'Factors of Attrition',
        positiveLabel: 'Factors of Hiring',
      },
    },
  },
  {
    position: { x: 33, width: 15, y: 0, height: 18 },
    id: 'role-sentiment-snapshot-positive-reviews',
    title: 'Positive Reviews',
    info: {
      text: 'An AI-generated summary of all positive reviews.',
    },
    plot: {
      defaultMetricOptions: ['positive_review'],
      isSingleEntity: true,
      chartType: 'sentiment_review',
    },
  },
  {
    position: { x: 33, width: 15, y: 18, height: 18 },
    id: 'role-sentiment-snapshot-negative-reviews',
    title: 'Negative Reviews',
    info: {
      text: 'An AI-generated summary of all negative reviews.',
    },
    plot: {
      defaultMetricOptions: ['negative_review'],
      isSingleEntity: true,
      chartType: 'sentiment_review',
    },
  },
];

const roleSentimentOvertime: TabContent['items'] = [
  {
    position: { x: 0, width: 11, y: 0, height: 12 },
    id: 'role-sentiment-overtime-overall',
    title: '{{metric}}',
    info: {
      text: sentimentInfoText,
    },
    plot: {
      chartType: 'line',
      defaultMetricOptions: roleSentimentMetrics,
    },
  },
  {
    position: { x: 11, width: 11, y: 0, height: 12 },
    id: 'role-sentiment-overtime-business-outlook',
    title: '{{metric}}',
    info: {
      text: sentimentInfoText,
    },
    plot: {
      chartType: 'line',
      defaultMetricOptions: roleSentimentMetrics,
    },
  },
  {
    position: { x: 22, width: 11, y: 0, height: 12 },
    id: 'role-sentiment-overtime-career-growth',
    title: '{{metric}}',
    info: {
      text: sentimentInfoText,
    },
    plot: {
      chartType: 'line',
      defaultMetricOptions: roleSentimentMetrics,
    },
  },
  {
    position: { x: 0, width: 11, y: 12, height: 12 },
    id: 'role-sentiment-overtime-compensation',
    title: '{{metric}}',
    info: {
      text: sentimentInfoText,
    },
    plot: {
      chartType: 'line',
      defaultMetricOptions: roleSentimentMetrics,
    },
  },
  {
    position: { x: 11, width: 11, y: 12, height: 12 },
    id: 'role-sentiment-overtime-benefits',
    title: '{{metric}}',
    info: {
      text: sentimentInfoText,
    },
    plot: {
      chartType: 'line',
      defaultMetricOptions: roleSentimentMetrics,
    },
  },
  {
    position: { x: 22, width: 11, y: 12, height: 12 },
    id: 'role-sentiment-overtime-culture',
    title: '{{metric}}',
    info: {
      text: sentimentInfoText,
    },
    plot: {
      chartType: 'line',
      defaultMetricOptions: roleSentimentMetrics,
    },
  },
  {
    position: { x: 0, width: 11, y: 24, height: 12 },
    id: 'role-sentiment-overtime-work-life-balance',
    title: '{{metric}}',
    info: {
      text: sentimentInfoText,
    },
    plot: {
      chartType: 'line',
      defaultMetricOptions: roleSentimentMetrics,
    },
  },
  {
    position: { x: 11, width: 11, y: 24, height: 12 },
    id: 'role-sentiment-overtime-diversity-and-inclusion',
    title: '{{metric}}',
    info: {
      text: sentimentInfoText,
    },
    plot: {
      chartType: 'line',
      defaultMetricOptions: roleSentimentMetrics,
    },
  },
  {
    position: { x: 22, width: 11, y: 24, height: 12 },
    id: 'role-sentiment-overtime-leadership',
    title: '{{metric}}',
    info: {
      text: sentimentInfoText,
    },
    plot: {
      chartType: 'line',
      defaultMetricOptions: roleSentimentMetrics,
    },
  },
  {
    position: { x: 33, width: 15, y: 0, height: 18 },
    id: 'role-sentiment-overtime-positive-reviews',
    title: 'Trending Positive Reviews',
    info: {
      text: 'An AI-generated summary of all positive reviews.',
    },
    plot: {
      defaultMetricOptions: ['positive_review'],
      isSingleEntity: true,
      chartType: 'sentiment_review',
    },
  },
  {
    position: { x: 33, width: 15, y: 18, height: 18 },
    id: 'role-sentiment-overtime-negative-reviews',
    title: 'Trending Negative Reviews',
    info: {
      text: 'An AI-generated summary of all negative reviews.',
    },
    plot: {
      defaultMetricOptions: ['negative_review'],
      isSingleEntity: true,
      chartType: 'sentiment_review',
    },
  },
];

const sharedConfig: Required<
  Pick<TabContent, 'primaryFilter' | 'secondaryFilters' | 'grid'>
> = {
  grid: { rows: DEFAULT_GRID_ROWS, columns: DEFAULT_GRID_COLUMNS },
  primaryFilter: {
    id: FilterId.ROLE,
  },
  secondaryFilters: [],
};

const roleSentimentSnapshotTab: TabContent = {
  id: 'role-sentiment-snapshot',
  items: roleSentimentSingleSnapshotItems,
  ...sharedConfig,
  secondaryFilters: [
    FilterId.OVERALL_RATING,
    FilterId.COMPANY,
    FilterId.INDUSTRY,
    FilterId.SENIORITY,
    FilterId.GEOGRAPHY,
    FilterId.SNAPSHOT_DATE,
  ].map((id) => ({ id })),
};

const roleSentimentOvertimeTab: TabContent = {
  id: 'role-sentiment-overtime',
  items: roleSentimentOvertime,
  ...sharedConfig,
  primaryFilter: { ...sharedConfig.primaryFilter, tagColors: true },
  secondaryFilters: [
    FilterId.OVERALL_RATING,
    FilterId.COMPANY,
    FilterId.INDUSTRY,
    FilterId.SENIORITY,
    FilterId.GEOGRAPHY,
    FilterId.DATE_RANGE,
  ].map((id) => ({ id })),
};

export const roleSentiment: Page = {
  type: PageType.REVELIO_PUBLIC,
  id: RevelioPageId.ROLE_SENTIMENT,
  revelioPageId: RevelioPageId.ROLE_SENTIMENT,
  name: 'Sentiment',
  owner: { id: 'revelio-labs', username: 'revelio-labs' },
  tabs: [
    {
      id: 'role-sentiment-snapshot',
      label: 'Snapshot',
      tabContent: roleSentimentSnapshotTab,
      isActive: true,
    },
    {
      id: 'role-sentiment-overtime',
      label: 'Over Time',
      tabContent: roleSentimentOvertimeTab,
      isActive: false,
    },
  ],
};

export const roleSentimentDefaultMetric: Record<string, MetricValues> = {
  'role-sentiment-snapshot-overall': 'overall',
  'role-sentiment-snapshot-business-outlook': 'business_outlook',
  'role-sentiment-snapshot-career-growth': 'career_growth',
  'role-sentiment-snapshot-compensation': 'compensation',
  'role-sentiment-snapshot-benefits': 'benefits',
  'role-sentiment-snapshot-culture': 'culture',
  'role-sentiment-snapshot-work-life-balance': 'work_life_balance',
  'role-sentiment-snapshot-diversity-and-inclusion': 'diversity_and_inclusion',
  'role-sentiment-snapshot-leadership': 'leadership',
  'role-sentiment-overtime-overall': 'overall',
  'role-sentiment-overtime-business-outlook': 'business_outlook',
  'role-sentiment-overtime-career-growth': 'career_growth',
  'role-sentiment-overtime-compensation': 'compensation',
  'role-sentiment-overtime-benefits': 'benefits',
  'role-sentiment-overtime-culture': 'culture',
  'role-sentiment-overtime-work-life-balance': 'work_life_balance',
  'role-sentiment-overtime-diversity-and-inclusion': 'diversity_and_inclusion',
  'role-sentiment-overtime-leadership': 'leadership',
};
